<template>
  <span
    v-ds-tooltip="props.description"
    :class="tokenClasses"
    data-cy="ds-inline-token"
    @click="handleClick"
  >
    <span
      v-if="hasPrefix"
      :class="DS_INLINE_TOKEN_CLASSES.Prefix"
      data-cy="ds-inline-token__prefix"
    >
      <DSUserAvatar
        v-if="props.avatarUrl"
        :src="props.avatarUrl"
        :size="DS_USER_SIZE.ExtraSmall"
        :status="DS_USER_STATUS.None"
        icon-name="user"
        alt=""
      />
      <DSFlag
        v-else-if="props.flag"
        :flag="props.flag"
        :size="DS_FLAG_SIZES.Small"
      />
      <DSIcon
        v-else-if="props.prefixIcon"
        :icon="props.prefixIcon"
        size="sm"
        aria-hidden="true"
      />
    </span>

    <span
      :class="DS_INLINE_TOKEN_CLASSES.Text"
      aria-hidden="true"
      data-cy="ds-inline-token__text"
    >
      {{ props.text }}
    </span>

    <span
      :class="DS_INLINE_TOKEN_CLASSES.Label"
      data-cy="ds-inline-token__label"
    >
      {{ tokenAriaLabel }}
    </span>
  </span>
</template>

<script setup lang="ts">
import { computed } from "vue";
import {
  DS_INLINE_TOKEN_GROUP_EDGE,
  DS_INLINE_TOKEN_VARIANT,
  type DSInlineTokenEmits,
  type DSInlineTokenProps,
} from "@/src/design_system/data_display/inline_token/types";
import {
  DS_INLINE_TOKEN_CLASSES,
  DS_INLINE_TOKEN_GROUP_EDGE_TO_CLASS_MAP,
  DS_INLINE_TOKEN_VARIANT_TO_CLASS_MAP,
  DS_INLINE_TOKEN_VARIANT_TO_DESCRIPTION_KEY,
} from "@/src/design_system/data_display/inline_token/constants";
import DSUserAvatar from "@/src/design_system/data_display/user/components/DSUserAvatar.vue";
import { DS_USER_SIZE } from "@/src/design_system/data_display/user/types/size";
import { DS_USER_STATUS } from "@/src/design_system/data_display/user/types/status";
import DSFlag from "@/src/design_system/primitives/flag/components/DSFlag.vue";
import { DS_FLAG_SIZES } from "@/src/design_system/primitives/flag/types";
import DSIcon from "@/src/design_system/primitives/icon/components/DSIcon.vue";
import { vDsTooltip } from "@/src/design_system/primitives/tooltip/directives/vDsTooltip";
import { DS_STATE_CLASSES } from "@/src/design_system/shared/constants/stateClasses";
import { useStateClasses } from "@/src/design_system/shared/composables/useStateClasses";

const props = withDefaults(defineProps<DSInlineTokenProps>(), {
  variant: DS_INLINE_TOKEN_VARIANT.Default,
  groupEdge: DS_INLINE_TOKEN_GROUP_EDGE.Solo,
  prefixIcon: undefined,
  avatarUrl: undefined,
  flag: undefined,
  selected: false,
  description: undefined,
  disabled: false,
  ariaLabel: undefined,
});

const emit = defineEmits<DSInlineTokenEmits>();

const { stateClasses } = useStateClasses(props);

const tokenClasses = computed(() => [
  DS_INLINE_TOKEN_CLASSES.Root,
  DS_INLINE_TOKEN_VARIANT_TO_CLASS_MAP[props.variant],
  DS_INLINE_TOKEN_GROUP_EDGE_TO_CLASS_MAP[props.groupEdge],
  stateClasses.value,
  { [DS_STATE_CLASSES.Selected]: props.selected },
]);

const hasPrefix = computed(() => Boolean(props.avatarUrl || props.flag || props.prefixIcon));

const tokenAriaLabel = computed(() => {
  const variantDescription =
    window.I18n.designSystem.inlineToken[DS_INLINE_TOKEN_VARIANT_TO_DESCRIPTION_KEY[props.variant]];
  const label = props.ariaLabel || `${props.text}, ${variantDescription}`;

  return props.description ? `${label}, ${props.description}` : label;
});

const handleClick = (event: MouseEvent) => {
  if (!props.disabled) {
    emit("click", event);
  }
};
</script>
